import {Platform} from 'react-native'
import {FFmpeg} from '@ffmpeg/ffmpeg'
import {fetchFile} from '@ffmpeg/util'

import {getFfmpegCoreUrls} from '#/lib/tellustv/ffmpegCore'

export type TVSegmentFile = {
  bytes: ArrayBuffer
  mimeType: string
  durationMs: number
  size: number
  objectUrl: string
}

export type TVSegmentResult = {
  segments: TVSegmentFile[]
  posterBlob: Blob
  posterObjectUrl: string
  width: number
  height: number
}

type SegmentOptions = {
  segmentSeconds?: number
  onProgress?: (progress: number) => void
}

export type TVSegmenterStep = 'ffmpeg_init' | 'segmenting'

export class TVSegmenterError extends Error {
  step: TVSegmenterStep
  url?: string
  status?: number
  constructor(
    message: string,
    step: TVSegmenterStep,
    options?: {url?: string; status?: number; cause?: unknown},
  ) {
    super(message)
    this.step = step
    this.url = options?.url
    this.status = options?.status
    if (options?.cause !== undefined) {
      ;(this as {cause?: unknown}).cause = options.cause
    }
  }
}

export function isTVSegmenterError(err: unknown): err is TVSegmenterError {
  if (!err || typeof err !== 'object') return false
  const step = (err as {step?: string}).step
  return step === 'ffmpeg_init' || step === 'segmenting'
}

let ffmpegInstance: FFmpeg | undefined

const SPA_FALLBACK_MESSAGE =
  'FFmpeg assets are being served as HTML (SPA fallback). Fix webpack historyApiFallback for /ffmpeg/* and ensure static files exist.'

function looksLikeHtml(contentType: string | null, buffer: ArrayBuffer) {
  const normalized = contentType?.toLowerCase() || ''
  if (normalized.includes('text/html')) return true
  const bytes = new Uint8Array(buffer.slice(0, 64))
  if (bytes.length >= 4) {
    if (
      bytes[0] === 0x00 &&
      bytes[1] === 0x61 &&
      bytes[2] === 0x73 &&
      bytes[3] === 0x6d
    ) {
      return false
    }
  }
  if (bytes.length === 0) return false
  const headerText =
    typeof TextDecoder !== 'undefined'
      ? new TextDecoder('utf-8').decode(bytes).toLowerCase()
      : String.fromCharCode(...bytes).toLowerCase()
  return headerText.includes('<!doctype') || headerText.includes('<html')
}

async function toBlobUrlNoStore(
  url: string,
  mimeType: string,
  {checkHtml}: {checkHtml?: boolean} = {},
) {
  let response: Response
  try {
    response = await fetch(url, {cache: 'no-store'})
  } catch (err) {
    throw new TVSegmenterError(
      `FFmpeg assets failed to load for ${url}.`,
      'ffmpeg_init',
      {url, cause: err},
    )
  }

  if (response.status === 304) {
    try {
      response = await fetch(url, {cache: 'reload'})
    } catch (err) {
      throw new TVSegmenterError(
        `FFmpeg assets failed to load for ${url}.`,
        'ffmpeg_init',
        {url, cause: err},
      )
    }
    if (response.status === 304) {
      try {
        response = await fetch(url, {cache: 'force-cache'})
      } catch (err) {
        throw new TVSegmenterError(
          `FFmpeg assets failed to load for ${url}.`,
          'ffmpeg_init',
          {url, cause: err},
        )
      }
    }
  }

  if (!response.ok) {
    throw new TVSegmenterError(
      `FFmpeg assets failed to load (status ${response.status}) for ${url}.`,
      'ffmpeg_init',
      {url, status: response.status},
    )
  }

  let buffer: ArrayBuffer
  try {
    buffer = await response.arrayBuffer()
  } catch (err) {
    throw new TVSegmenterError(
      `FFmpeg assets failed to load for ${url}.`,
      'ffmpeg_init',
      {url, cause: err},
    )
  }

  if (checkHtml && looksLikeHtml(response.headers.get('content-type'), buffer)) {
    throw new TVSegmenterError(SPA_FALLBACK_MESSAGE, 'ffmpeg_init', {url})
  }

  try {
    const blob = new Blob([buffer], {type: mimeType})
    return URL.createObjectURL(blob)
  } catch (err) {
    throw new TVSegmenterError(
      `FFmpeg assets failed to load for ${url}.`,
      'ffmpeg_init',
      {url, cause: err},
    )
  }
}

async function getFFmpeg() {
  if (ffmpegInstance) return ffmpegInstance
  const ffmpeg = new FFmpeg()
  const {coreJsUrl, coreWasmUrl, coreWorkerUrl} = getFfmpegCoreUrls()
  const coreURL = await toBlobUrlNoStore(coreJsUrl, 'text/javascript')
  const wasmURL = await toBlobUrlNoStore(coreWasmUrl, 'application/wasm', {
    checkHtml: true,
  })
  const workerURL = await toBlobUrlNoStore(coreWorkerUrl, 'text/javascript')
  try {
    await ffmpeg.load({coreURL, wasmURL, workerURL})
  } catch (err) {
    throw new TVSegmenterError('FFmpeg failed to initialize.', 'ffmpeg_init', {
      cause: err,
    })
  }
  ffmpegInstance = ffmpeg
  return ffmpeg
}

async function getVideoMetadata(file: File) {
  return new Promise<{width: number; height: number; durationMs: number}>(
    (resolve, reject) => {
      const video = document.createElement('video')
      const url = URL.createObjectURL(file)
      video.preload = 'metadata'
      video.src = url
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url)
        resolve({
          width: video.videoWidth,
          height: video.videoHeight,
          durationMs: video.duration * 1000,
        })
      }
      video.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('Failed to read video metadata'))
      }
    },
  )
}

async function getDurationMsForBlob(blob: Blob): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const url = URL.createObjectURL(blob)
    video.preload = 'metadata'
    video.src = url
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url)
      resolve(video.duration * 1000)
    }
    video.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to read segment metadata'))
    }
  })
}

export async function segmentVideoWeb(
  file: File,
  {segmentSeconds = 180, onProgress}: SegmentOptions = {},
): Promise<TVSegmentResult> {
  let progressListener: ((p: {progress: number}) => void) | undefined
  let logListener: ((log: {type: string; message: string}) => void) | undefined
  let ffmpeg: FFmpeg | undefined
  try {
    ffmpeg = await getFFmpeg()

    if (__DEV__) {
      logListener = ({type, message}) => {
        console.info('[ffmpeg]', type, message)
      }
      ffmpeg.on('log', logListener)
    }

    if (onProgress) {
      progressListener = ({progress}) => {
        onProgress(progress)
      }
      ffmpeg.on('progress', progressListener)
    }

    const removeOldOutputs = async () => {
      const entries = await ffmpeg.listDir('/')
      for (const entry of entries) {
        if (
          entry.name.startsWith('segment_') ||
          entry.name === 'poster.jpg'
        ) {
          try {
            await ffmpeg.deleteFile(entry.name)
          } catch {
            // ignore cleanup failures
          }
        }
      }
    }

    const inputName = 'input.mp4'
    await removeOldOutputs()
    await ffmpeg.writeFile(inputName, await fetchFile(file))

    const segmentPattern = 'segment_%03d.mp4'
    const segmentCopyArgs = [
      '-i',
      inputName,
      '-c',
      'copy',
      '-f',
      'segment',
      '-segment_time',
      String(segmentSeconds),
      '-reset_timestamps',
      '1',
      segmentPattern,
    ]
    const segmentEncodeArgs = [
      '-i',
      inputName,
      '-c:v',
      'libx264',
      '-preset',
      'veryfast',
      '-pix_fmt',
      'yuv420p',
      '-c:a',
      'aac',
      '-b:a',
      '128k',
      '-f',
      'segment',
      '-segment_time',
      String(segmentSeconds),
      '-reset_timestamps',
      '1',
      segmentPattern,
    ]
    try {
      if (__DEV__) {
        console.info('[ffmpeg] exec segmentation (copy)', segmentCopyArgs)
      }
      await ffmpeg.exec(segmentCopyArgs)
    } catch {
      if (__DEV__) {
        console.info('[ffmpeg] exec segmentation (re-encode)', segmentEncodeArgs)
      }
      await ffmpeg.exec(segmentEncodeArgs)
    }

    const afterSegmentEntries = await ffmpeg.listDir('/')
    if (__DEV__) {
      console.info(
        '[ffmpeg] files after segmentation',
        afterSegmentEntries.map(entry => entry.name),
      )
    }

    const posterArgs = [
      '-i',
      inputName,
      '-ss',
      '1',
      '-frames:v',
      '1',
      '-vf',
      'scale=1280:720:force_original_aspect_ratio=decrease,pad=1280:720:(ow-iw)/2:(oh-ih)/2',
      'poster.jpg',
    ]
    const posterFallbackArgs = [
      '-i',
      inputName,
      '-ss',
      '0',
      '-frames:v',
      '1',
      '-vf',
      'scale=1280:720:force_original_aspect_ratio=decrease,pad=1280:720:(ow-iw)/2:(oh-ih)/2',
      'poster.jpg',
    ]

    try {
      if (__DEV__) {
        console.info('[ffmpeg] exec poster', posterArgs)
      }
      await ffmpeg.exec(posterArgs)
    } catch {
      if (__DEV__) {
        console.info('[ffmpeg] exec poster fallback', posterFallbackArgs)
      }
      await ffmpeg.exec(posterFallbackArgs)
    }

    const files = await ffmpeg.listDir('/')
    if (__DEV__) {
      console.info(
        '[ffmpeg] files after poster',
        files.map(entry => entry.name),
      )
    }

    const segmentFiles = files
      .filter(
        entry =>
          entry.name.startsWith('segment_') && entry.name.endsWith('.mp4'),
      )
      .map(entry => entry.name)
      .sort()
    const hasPoster = files.some(entry => entry.name === 'poster.jpg')

    if (!segmentFiles.length || !hasPoster) {
      const names = files.map(entry => entry.name).sort()
      throw new TVSegmenterError(
        `No segments produced. Files: ${
          names.length ? names.join(', ') : '(empty)'
        }`,
        'segmenting',
      )
    }

    const segments: TVSegmentFile[] = []
    for (const name of segmentFiles) {
      const data = await ffmpeg.readFile(name)
      const bytes = data.buffer.slice(
        data.byteOffset,
        data.byteOffset + data.byteLength,
      )
      const blob = new Blob([bytes], {type: 'video/mp4'})
      const durationMs = await getDurationMsForBlob(blob)
      const objectUrl = URL.createObjectURL(blob)
      segments.push({
        bytes,
        mimeType: 'video/mp4',
        durationMs,
        size: blob.size,
        objectUrl,
      })
    }

    const posterData = await ffmpeg.readFile('poster.jpg')
    const posterBytes = posterData.buffer.slice(
      posterData.byteOffset,
      posterData.byteOffset + posterData.byteLength,
    )
    const posterBlob = new Blob([posterBytes], {type: 'image/jpeg'})
    const posterObjectUrl = URL.createObjectURL(posterBlob)

    const {width, height} = await getVideoMetadata(file)

    return {
      segments,
      posterBlob,
      posterObjectUrl,
      width,
      height,
    }
  } catch (err) {
    if (isTVSegmenterError(err)) throw err
    const message =
      err instanceof Error ? err.message : 'Failed to segment video.'
    throw new TVSegmenterError(message, 'segmenting', {cause: err})
  } finally {
    if (progressListener) {
      try {
        const instance = ffmpeg || (await getFFmpeg())
        instance.off('progress', progressListener)
      } catch {
        // ignore cleanup failures
      }
    }
    if (logListener && ffmpeg) {
      try {
        ffmpeg.off('log', logListener)
      } catch {
        // ignore cleanup failures
      }
    }
  }
}

export async function segmentVideo(
  file: File,
  opts?: SegmentOptions,
): Promise<TVSegmentResult> {
  if (Platform.OS !== 'web') {
    throw new Error('Tellus TV upload is only available on web for now.')
  }
  return segmentVideoWeb(file, opts)
}
